/**
 * Utility functions for API client
 */

/**
 * Result of path replacement
 */
export interface PathReplacementResult {
  /** URL with path parameters replaced */
  url: string;
  /** Remaining parameters not used in path */
  remainingParams: Record<string, any>;
}

/**
 * Replace path parameters in URL template
 * @param path URL template with :param placeholders
 * @param params Parameters object
 * @returns Replaced URL and remaining parameters
 *
 * @example
 * replacePath('/user/:id', { id: '123', name: 'foo' })
 * // Returns: { url: '/user/123', remainingParams: { name: 'foo' } }
 */
export function replacePath(path: string, params: Record<string, any> = {}): PathReplacementResult {
  const remainingParams = { ...params };

  // Replace :param with actual values
  const url = path.replace(/:([^/]+)/g, (_, key) => {
    const value = remainingParams[key];
    if (value === undefined) {
      throw new Error(`Missing path parameter: ${key}`);
    }
    delete remainingParams[key];
    return encodeURIComponent(String(value));
  });

  return { url, remainingParams };
}

/**
 * Build query string from parameters
 * @param params Parameters object
 * @returns Query string (without leading ?)
 *
 * @example
 * buildQueryString({ name: 'foo', age: 18 })
 * // Returns: 'name=foo&age=18'
 */
export function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  }

  return searchParams.toString();
}

/**
 * Append query string to URL
 * @param url Base URL
 * @param params Parameters object
 * @returns URL with query string
 */
export function appendQueryString(url: string, params: Record<string, any>): string {
  const queryString = buildQueryString(params);
  if (!queryString) {
    return url;
  }

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${queryString}`;
}
